import type { ActionImpactDelta, ActionImpactMetrics, ActionImpactV1 } from "@adpilot/shared";
import {
  IconAlertTriangle,
  IconArrowRight,
  IconChartDots3,
  IconCircleCheck,
  IconClock,
  IconInfoCircle,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ControlButton } from "@/components/patterns/control-button";
import { EmptyState } from "@/components/shared/empty-state";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { fetchActionImpact } from "@/lib/action-impact-api";
import { useFormatters } from "@/lib/format";
import { cn } from "@/lib/utils";

interface ActionImpactSheetProps {
  actionId: string | null;
  targetName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type ImpactMetricKey = keyof ActionImpactMetrics;
type DeltaMetricKey = keyof ActionImpactDelta;

const METRICS: Array<{ key: ImpactMetricKey; deltaKey?: DeltaMetricKey }> = [
  { key: "spend", deltaKey: "spend" },
  { key: "sales", deltaKey: "sales" },
  { key: "orders", deltaKey: "orders" },
  { key: "clicks", deltaKey: "clicks" },
  { key: "impressions", deltaKey: "impressions" },
  { key: "acos", deltaKey: "acos" },
  { key: "roas", deltaKey: "roas" },
  { key: "cvr", deltaKey: "cvr" },
  { key: "cpc", deltaKey: "cpc" },
];

export function impactDeltaTone(
  metric: DeltaMetricKey,
  value: number | null,
): "success" | "destructive" | "neutral" {
  if (value === null || value === 0 || metric === "clicks" || metric === "impressions") {
    return "neutral";
  }
  const lowerIsBetter = metric === "spend" || metric === "acos" || metric === "cpc";
  return (lowerIsBetter && value < 0) || (!lowerIsBetter && value > 0) ? "success" : "destructive";
}

export function ActionImpactSheet({
  actionId,
  targetName,
  open,
  onOpenChange,
}: ActionImpactSheetProps): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const query = useQuery({
    queryKey: ["ai-actions", actionId, "impact"],
    queryFn: () => {
      if (!actionId) throw new Error("Action Impact requires an action ID");
      return fetchActionImpact(actionId);
    },
    enabled: open && actionId !== null,
  });

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-[448px] max-w-[448px] gap-0 rounded-none data-[side=right]:sm:max-w-[448px]"
        showCloseButton={false}
      >
        <div className="flex min-h-[103px] shrink-0 items-start justify-between border-b border-line px-5 py-5">
          <div className="min-w-0">
            <p className="text-xs leading-4 text-muted-foreground">
              {t("runDetail.decisionRecord.impact.eyebrow")}
            </p>
            <SheetTitle className="mt-1 truncate text-lg leading-6">{targetName}</SheetTitle>
            <SheetDescription className="mt-0.5 text-xs leading-4">
              {t("runDetail.decisionRecord.impact.description")}
            </SheetDescription>
          </div>
          <ControlButton
            controlSize="icon-compact"
            variant="outline"
            aria-label={t("runDetail.decisionRecord.impact.close")}
            onClick={() => onOpenChange(false)}
          >
            <IconX />
          </ControlButton>
        </div>

        <div className="paper-scrollbar min-h-0 flex-1 overflow-y-auto px-5 py-4">
          {query.isLoading ? (
            <ImpactSkeleton />
          ) : query.isError || !query.data ? (
            <Alert variant="warning">
              <IconAlertTriangle />
              <AlertTitle>{t("runDetail.decisionRecord.impact.errorTitle")}</AlertTitle>
              <AlertDescription className="space-y-3">
                <p>{t("runDetail.decisionRecord.impact.errorDescription")}</p>
                <Button size="xs" variant="outline" onClick={() => void query.refetch()}>
                  <IconRefresh data-icon="inline-start" />
                  {t("runDetail.decisionRecord.impact.retry")}
                </Button>
              </AlertDescription>
            </Alert>
          ) : (
            <ActionImpactContent impact={query.data} />
          )}
        </div>

        <div className="flex min-h-[65px] shrink-0 items-center justify-end border-t border-line px-5 py-3.5">
          <ControlButton
            controlSize="toolbar"
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            {t("runDetail.decisionRecord.impact.close")}
          </ControlButton>
        </div>
      </SheetContent>
    </Sheet>
  );
}

export function ActionImpactContent({ impact }: { impact: ActionImpactV1 }): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  const { formatCurrency, formatDate, formatNumber, formatPercent } = useFormatters();
  const expectedDays = impact.statusDetail.expectedDays;
  const waiting = impact.dataStatus === "waiting_for_after_window";

  function formatMoney(value: number): string {
    return impact.currencyCode
      ? formatCurrency(value, impact.currencyCode)
      : formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  if (impact.dataStatus === "insufficient_metrics") {
    return (
      <EmptyState
        icon={<IconChartDots3 className="size-9" />}
        title={t("runDetail.decisionRecord.impact.insufficientTitle")}
        description={impact.statusDetail.reasons
          .map((item) => t(`runDetail.decisionRecord.impact.reasons.${item}`))
          .join(" ")}
      />
    );
  }

  function formatMetric(key: ImpactMetricKey, value: number | null): string {
    if (value === null) return "—";
    if (key === "spend" || key === "sales" || key === "cpc") return formatMoney(value);
    if (key === "acos" || key === "cvr") return formatPercent(value);
    return formatNumber(value, { maximumFractionDigits: key === "roas" ? 2 : 0 });
  }

  function formatDelta(key: DeltaMetricKey, value: number | null): string {
    if (value === null) return "—";
    const sign = value > 0 ? "+" : value < 0 ? "−" : "";
    const absolute = Math.abs(value);
    if (key === "spend" || key === "sales" || key === "cpc") {
      return `${sign}${formatMoney(absolute)}`;
    }
    if (key === "acos" || key === "cvr") return `${sign}${formatPercent(absolute)}`;
    return `${sign}${formatNumber(absolute, { maximumFractionDigits: key === "roas" ? 2 : 0 })}`;
  }

  return (
    <div className="flex flex-col gap-3.5">
      <div className="flex min-h-[70px] items-center justify-between rounded-[10px] border border-line px-3.5 py-3">
        <ImpactBid
          currencyCode={impact.currencyCode}
          label={t("runDetail.decisionRecord.impact.beforeBid")}
          value={impact.oldBid}
        />
        <IconArrowRight className="size-4 text-muted-foreground" />
        <ImpactBid
          align="right"
          currencyCode={impact.currencyCode}
          label={t("runDetail.decisionRecord.impact.executedBid")}
          value={impact.newBid}
        />
      </div>

      {waiting ? (
        <Alert variant="warning">
          <IconClock />
          <AlertTitle>{t("runDetail.decisionRecord.impact.waitingTitle")}</AlertTitle>
          <AlertDescription>
            {t("runDetail.decisionRecord.impact.waitingDescription", {
              date: formatDate(impact.statusDetail.afterWindowEndDate),
            })}
          </AlertDescription>
        </Alert>
      ) : (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <IconCircleCheck className="size-4 text-success-foreground" />
            <p className="text-xs font-semibold">
              {t("runDetail.decisionRecord.impact.readyTitle")}
            </p>
          </div>
          <Badge variant="success" size="compact">
            {t("runDetail.decisionRecord.impact.ready")}
          </Badge>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2">
        <WindowCard
          label={t("runDetail.decisionRecord.impact.beforeWindow")}
          start={impact.before.startDate}
          end={impact.before.endDate}
          covered={impact.before.daysCovered}
          expected={expectedDays}
        />
        <WindowCard
          label={t("runDetail.decisionRecord.impact.afterWindow")}
          start={impact.after.startDate}
          end={impact.after.endDate}
          covered={impact.after.daysCovered}
          expected={expectedDays}
        />
      </div>

      <div className="overflow-hidden rounded-[10px] border border-line">
        <div className="grid grid-cols-[1fr_88px_88px_76px] bg-subtle px-3 py-2 text-[9px] font-medium text-muted-foreground uppercase">
          <span>{t("runDetail.decisionRecord.impact.metric")}</span>
          <span>{t("runDetail.decisionRecord.impact.before")}</span>
          <span>{t("runDetail.decisionRecord.impact.after")}</span>
          <span className="text-right">{t("runDetail.decisionRecord.impact.change")}</span>
        </div>
        {METRICS.map(({ key, deltaKey }) => {
          const delta = impact.delta && deltaKey ? impact.delta[deltaKey] : null;
          const tone = deltaKey ? impactDeltaTone(deltaKey, delta) : "neutral";
          return (
            <div
              key={key}
              className="grid min-h-11 grid-cols-[1fr_88px_88px_76px] items-center border-t border-line px-3 text-[10px]"
            >
              <span className="text-muted-foreground">
                {t(`runDetail.decisionRecord.impact.metrics.${key}`)}
              </span>
              <span className="font-mono font-medium tabular-nums">
                {formatMetric(key, impact.before.metrics[key])}
              </span>
              <span className="font-mono font-semibold tabular-nums">
                {formatMetric(key, impact.after.metrics[key])}
              </span>
              <span
                className={cn(
                  "text-right font-mono font-medium tabular-nums",
                  !waiting && tone === "success" && "text-success-foreground",
                  !waiting && tone === "destructive" && "text-destructive-foreground",
                  (waiting || tone === "neutral") && "text-muted-foreground",
                )}
              >
                {!waiting && deltaKey ? formatDelta(deltaKey, delta) : "—"}
              </span>
            </div>
          );
        })}
      </div>

      <div className="flex items-start gap-2 rounded-[8px] bg-subtle px-3 py-2.5 text-[10px] leading-4 text-muted-foreground">
        <IconInfoCircle className="mt-0.5 size-3.5 shrink-0" />
        <p>{t("runDetail.decisionRecord.impact.disclaimer")}</p>
      </div>
    </div>
  );
}

function ImpactBid({
  align = "left",
  currencyCode,
  label,
  value,
}: {
  align?: "left" | "right";
  currencyCode: string | null;
  label: string;
  value: number | null;
}): React.ReactElement {
  const { formatCurrency, formatNumber } = useFormatters();
  return (
    <div className={align === "right" ? "text-right" : undefined}>
      <p className="text-[9px] leading-3 text-muted-foreground uppercase">{label}</p>
      <p className="mt-1 font-mono text-base leading-5 font-semibold tabular-nums">
        {value === null
          ? "—"
          : currencyCode
            ? formatCurrency(value, currencyCode)
            : formatNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      </p>
    </div>
  );
}

function WindowCard({
  covered,
  end,
  expected,
  label,
  start,
}: {
  covered: number;
  end: string;
  expected: number;
  label: string;
  start: string;
}): React.ReactElement {
  const { t } = useTranslation("aiOperations");
  return (
    <div className="rounded-[8px] bg-subtle px-3 py-2.5">
      <div className="flex items-center justify-between gap-2">
        <p className="text-[9px] text-muted-foreground uppercase">{label}</p>
        {covered < expected ? (
          <Badge variant="warning" size="compact">
            {t("runDetail.decisionRecord.impact.incomplete", { covered, expected })}
          </Badge>
        ) : null}
      </div>
      <p className="mt-1 font-mono text-[10px] font-medium tabular-nums">
        {start} – {end}
      </p>
    </div>
  );
}

function ImpactSkeleton(): React.ReactElement {
  return (
    <div className="space-y-3">
      <Skeleton className="h-[70px] w-full" />
      <Skeleton className="h-10 w-full" />
      <div className="grid grid-cols-2 gap-2">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
      <Skeleton className="h-[420px] w-full" />
    </div>
  );
}
